/**
 * Forgot Password Modal Component
 * Handles password reset requests for both agents and homeowners
 */
import React, { useState } from 'react';
import { useTheme } from '../ThemeContext';
import { getApiUrl } from '../utils/platform';

const ForgotPassword = ({ onClose, userType }) => {
  const { colors } = useTheme();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(getApiUrl('/.netlify/functions/reset-password'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.trim(),
          userType: userType // 'agent' or 'client'
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
      } else {
        setError(data.error || 'Failed to send reset email');
      }
    } catch (err) {
      console.error('Password reset error:', err);
      setError('Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div style={styles.overlay}>
        <div style={{...styles.modal, background: colors.surface, border: `1px solid ${colors.border}`}}>
          <div style={styles.header}>
            <h2 style={{...styles.title, color: colors.text}}>Check Your Email</h2>
            <button
              onClick={onClose}
              style={{...styles.closeButton, color: colors.textSecondary}}
            >
              ✕
            </button>
          </div>

          <div style={styles.content}>
            <div style={{...styles.successIcon, color: '#59c4c4'}}>📧</div>
            <p style={{...styles.successMessage, color: colors.text}}>
              If an account exists with <strong>{email}</strong>, a password reset link has been sent.
            </p>
            <p style={{...styles.helpText, color: colors.textSecondary}}>
              Check your inbox and spam folder. The link will expire in 1 hour.
            </p>
            <button
              onClick={onClose}
              style={{...styles.button, ...styles.primaryButton}}
            >
              Got It
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.overlay}>
      <div style={{...styles.modal, background: colors.surface, border: `1px solid ${colors.border}`}}>
        <div style={styles.header}>
          <h2 style={{...styles.title, color: colors.text}}>Forgot Password</h2>
          <button
            onClick={onClose}
            style={{...styles.closeButton, color: colors.textSecondary}}
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} style={styles.content}>
          <p style={{...styles.description, color: colors.textSecondary}}>
            Enter your email address and we'll send you a link to reset your password.
          </p>

          {error && (
            <div style={{...styles.errorBox, background: '#fee', border: '1px solid #fcc', color: '#c00'}}>
              {error}
            </div>
          )}

          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email"
            style={{...styles.input, background: colors.surface, border: `2px solid ${colors.borderLight}`, color: colors.text}}
            required
            autoFocus
          />

          <div style={styles.buttonGroup}>
            <button
              type="button"
              onClick={onClose}
              style={{...styles.button, ...styles.secondaryButton, borderColor: colors.border, color: colors.text}}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{...styles.button, ...styles.primaryButton}}
              disabled={loading}
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
    padding: '1rem',
    backdropFilter: 'blur(4px)'
  },
  modal: {
    borderRadius: '16px',
    maxWidth: '480px',
    width: '100%',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    overflow: 'hidden'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1.5rem 1.5rem 1rem'
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: 400,
    margin: 0,
    letterSpacing: '0.02em'
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '1.5rem',
    cursor: 'pointer',
    padding: '0.25rem',
    lineHeight: 1,
    opacity: 0.6,
    transition: 'opacity 0.2s'
  },
  content: {
    padding: '0 1.5rem 1.5rem'
  },
  description: {
    fontSize: '0.95rem',
    lineHeight: '1.6',
    marginBottom: '1.5rem'
  },
  input: {
    width: '100%',
    padding: '0.9rem 1.1rem',
    fontSize: '1rem',
    borderRadius: '12px',
    marginBottom: '1.5rem',
    textAlign: 'left',
    boxSizing: 'border-box',
    transition: 'all 0.2s ease',
    fontFamily: 'inherit'
  },
  buttonGroup: {
    display: 'flex',
    gap: '0.75rem',
    justifyContent: 'flex-end'
  },
  button: {
    padding: '0.75rem 1.5rem',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: 500,
    fontSize: '0.95rem',
    transition: 'all 0.2s ease',
    letterSpacing: '0.02em'
  },
  primaryButton: {
    background: 'linear-gradient(135deg, #59c4c4 0%, #4ab3b3 100%)',
    color: 'white',
    boxShadow: '0 2px 8px rgba(89, 196, 196, 0.3)'
  },
  secondaryButton: {
    background: 'transparent',
    border: '2px solid'
  },
  errorBox: {
    padding: '0.75rem 1rem',
    borderRadius: '8px',
    marginBottom: '1rem',
    fontSize: '0.9rem'
  },
  successIcon: {
    fontSize: '4rem',
    textAlign: 'center',
    marginBottom: '1rem'
  },
  successMessage: {
    fontSize: '1rem',
    lineHeight: '1.6',
    textAlign: 'center',
    marginBottom: '1rem'
  },
  helpText: {
    fontSize: '0.9rem',
    lineHeight: '1.6',
    textAlign: 'center',
    marginBottom: '1.5rem'
  }
};

export default ForgotPassword;
